import { set_token, set_user_details, set_user_profile, fetch_and_store_user_profile } from '@/router';
import SnackbarMessage from "@/components/SnackbarMessage/SnackbarMessage.vue";

// login url
const login_url = process.env.VUE_APP_LOGIN_URL;
const client_app = process.env.VUE_APP_CLIENT_APP;

function store_otp_timer_metadata(data) {
  sessionStorage.removeItem('pending_otp_expires_at');
  sessionStorage.removeItem('pending_otp_expires_in_seconds');
  sessionStorage.removeItem('pending_otp_server_time');
  sessionStorage.setItem('pending_otp_timer_received_at', String(Date.now()));

  if (data.otp_expires_at) {
    sessionStorage.setItem('pending_otp_expires_at', data.otp_expires_at);
  }

  if (data.server_time) {
    sessionStorage.setItem('pending_otp_server_time', data.server_time);
  }

  if (data.otp_expires_in_seconds !== undefined && data.otp_expires_in_seconds !== null) {
    sessionStorage.setItem('pending_otp_expires_in_seconds', String(data.otp_expires_in_seconds));
  }
}

function get_error_payload(data) {
  return data?.errors || data?.detail || data?.error || data?.message || data;
}

function stringify_error_payload(payload) {
  if (!payload) {
    return '';
  }

  if (typeof payload === 'string') {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload.map((item) => stringify_error_payload(item)).filter(Boolean).join(', ');
  }

  if (typeof payload === 'object') {
    return Object.entries(payload)
      .map(([field, messages]) => {
        const label = String(field || '').replace(/[_-]+/g, ' ');
        return `${label}: ${stringify_error_payload(messages)}`;
      })
      .join(' ');
  }

  return String(payload);
}

function get_login_error_context(error) {
  const raw_message = stringify_error_payload(error?.payload || error?.message || error);

  return ({
    status: error?.status || null,
    raw_message,
    message: raw_message.toLowerCase()
  });
}

const login_status_error_messages = {
  401: 'Those login details did not match our records. Please check your email and password.',
  403: 'This account cannot sign in right now.',
  404: 'We could not find the login service. Please try again later.'
};

const login_message_error_messages = {
  invalid_credentials: 'Those login details did not match our records. Please check your email and password.',
  'invalid response format from server': 'The login server sent something unexpected. Please try again in a moment.',
  'no otp challenge received from server': 'We could not start verification for this login. Please try signing in again.',
  'failed to fetch': 'We could not reach the login server. Please check your connection and try again.',
  networkerror: 'We could not reach the login server. Please check your connection and try again.'
};

function get_configured_login_error(message) {
  return !login_url || message.includes('login endpoint is not configured')
    ? 'Login is not fully set up yet. Please check the app configuration.'
    : '';
}

function get_status_login_error({ status, raw_message }) {
  if (status === 400) {
    return raw_message || 'Please check your login details and try again.';
  }

  if (status >= 500) {
    return 'The login server is having trouble right now. Please try again in a moment.';
  }

  return login_status_error_messages[String(status)] || '';
}

function get_friendly_login_error(error, fallback_message = 'We could not sign you in. Please try again.') {
  const context = get_login_error_context(error);
  const mapped_message = get_configured_login_error(context.message)
    || login_message_error_messages[context.message]
    || get_status_login_error(context);

  return mapped_message || context.raw_message || fallback_message;
}

async function parse_login_response(response) {
  const raw_response = await response.text();

  if (!raw_response) {
    return {};
  }

  let data = JSON.parse(raw_response);

  if (typeof data === 'string') {
    data = JSON.parse(data);
  }

  return data;
}

export default {
  name: 'LoginView',
  components: {
    SnackbarMessage
  },
  data() {
    return ({
      credentials: {
        email: '',
        password: ''
      },
      password_input_type: 'password',
      is_loading: false,
      error_message: '',
      success_message: '',
      snackbar_timeout_id: null,
      login_url: login_url
    });
  },
  beforeUnmount() {
    this.clear_snackbar_timer();
  },
  methods: {
    toggle_change_password_type() {
      this.password_input_type = this.password_input_type === 'password' ? 'text' : 'password';
    },

    clear_snackbar_timer() {
      if (this.snackbar_timeout_id) {
        clearTimeout(this.snackbar_timeout_id);
        this.snackbar_timeout_id = null;
      }
    },

    start_snackbar_timer() {
      this.clear_snackbar_timer();
      this.snackbar_timeout_id = setTimeout(() => {
        this.error_message = '';
        this.success_message = '';
        this.snackbar_timeout_id = null;
      }, 5000);
    },

    clear_error_message() {
      this.clear_snackbar_timer();
      this.error_message = '';
    },

    clear_success_message() {
      this.clear_snackbar_timer();
      this.success_message = '';
    },

    set_error_message(error, fallback_message) {
      this.error_message = get_friendly_login_error(error, fallback_message);
      this.success_message = '';
      this.start_snackbar_timer();
    },

    set_success_message(message) {
      this.success_message = message || '';
      this.error_message = '';
      if (message) {
        this.start_snackbar_timer();
      }
    },

    clear_pending_login() {
      sessionStorage.removeItem('pending_user_data');
      sessionStorage.removeItem('pending_username');
      sessionStorage.removeItem('pending_challenge_token');
      sessionStorage.removeItem('pending_login_password');
      sessionStorage.removeItem('pending_otp_expires_at');
      sessionStorage.removeItem('pending_otp_expires_in_seconds');
      sessionStorage.removeItem('pending_otp_server_time');
      sessionStorage.removeItem('pending_otp_timer_received_at');
    },

    async complete_token_login(data) {
      if (!data.token) {
        throw new Error('No authentication token received.');
      }

      const user = data.user || {};
      const role_name = user.user_role && user.user_role.role
        ? user.user_role.role.name
        : null;

      set_token(data.token);
      set_user_details({
        role_type: role_name,
        user_id: user.id,
        first_name: user.first_name || '',
        last_name: user.last_name || ''
      });
      set_user_profile(user);
      await fetch_and_store_user_profile();
      this.clear_pending_login();
      this.set_success_message(data.message || 'Login successful.');
      this.$router.push('/home');
    },

    async handle_login() {
      try {
        this.is_loading = true;
        this.error_message = '';
        this.success_message = '';

        // Validate inputs
        if (!this.credentials.email || !this.credentials.password) {
          this.set_error_message('Please enter your email and password to continue.');
          this.is_loading = false;
          return;
        }

        if (!this.login_url) {
          throw new Error('Login endpoint is not configured.');
        }

        // Make login request
        const response = await fetch(this.login_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            username: this.credentials.email,
            password: this.credentials.password,
            client_app
          })
        });

        // Parse response
        let data;

        try {
          data = await parse_login_response(response);
        } catch (e) {
          throw new Error('Invalid response format from server');
        }

        if (!response.ok) {
          const error = new Error('Login request failed.');
          error.status = response.status;
          error.payload = get_error_payload(data);
          throw error;
        }

        // Check if authentication was successful
        if (data.status !== 'success') {
          const error = new Error(data.message || 'Authentication failed');
          error.payload = get_error_payload(data);
          throw error;
        }

        if (data.otp_required === false || data.token) {
          await this.complete_token_login(data);
          this.is_loading = false;
          return;
        }

        if (!data.challenge_token) {
          throw new Error('No OTP challenge received from server');
        }

        sessionStorage.setItem('pending_user_data', JSON.stringify(data.user || {}));
        sessionStorage.setItem('pending_username', this.credentials.email);
        sessionStorage.setItem('pending_challenge_token', data.challenge_token);
        sessionStorage.setItem('pending_login_password', this.credentials.password);
        store_otp_timer_metadata(data);
        this.set_success_message('Verification code sent. Check your email to continue.');
        
        // Redirect to OTP Verification Screen
        this.$router.push('/one_time_pin');
        
        // Clear loading state after push
        this.is_loading = false;
        return;

      } catch (error) {
        this.set_error_message(error, 'We could not sign you in. Please try again.');
        this.is_loading = false;
      }
    }
  }
}
